/**
 * VehicleRegLookup - inline reg-plate auto-fill + editable specs card.
 *
 * Renders on the Post-an-Ad form only when the seller has picked a category
 * whose parent is `vehicles`. On successful lookup, populates 12-22 spec fields
 * pulled from DVLA VES + DVSA MOT + CarQueryAPI. Every field remains editable
 * so the seller can correct any wrong data before submitting the listing.
 *
 * Props:
 *   parentCategory: string   - top-level category slug (e.g. "vehicles")
 *   value: object            - current specs (parent state)
 *   onChange: (specs) => void
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, Search, AlertCircle, CheckCircle2, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// The 22 editable spec fields, grouped for the UI
const FIELD_GROUPS = [
  {
    heading: 'Overview',
    fields: [
      ['make', 'Make'],
      ['model', 'Model'],
      ['year', 'Year'],
      ['colour', 'Colour'],
      ['mileage', 'Mileage (miles)'],
      ['seats', 'Seats'],
      ['body_type', 'Body Type'],
      ['doors', 'Doors'],
      ['transmission', 'Transmission'],
      ['luggage_capacity', 'Luggage Capacity'],
    ],
  },
  {
    heading: 'Performance',
    fields: [
      ['engine_size', 'Engine Size'],
      ['engine_power', 'Engine Power'],
      ['torque', 'Torque'],
      ['acceleration', '0-60 mph'],
      ['top_speed', 'Top Speed'],
      ['drive_type', 'Drive Type'],
      ['fuel_type', 'Fuel Type'],
      ['cylinders', 'Cylinders'],
    ],
  },
  {
    heading: 'Running Costs',
    fields: [
      ['fuel_economy', 'Fuel Economy (mpg)'],
      ['insurance_group', 'Insurance Group'],
      ['tax_per_year', 'Road Tax /yr (£)'],
      ['co2_emissions', 'CO₂ Emissions'],
      ['euro_emissions', 'Euro Standard'],
      ['service_cost', 'Annual Service Cost (£)'],
    ],
  },
];

export default function VehicleRegLookup({ value = {}, onChange }) {
  const [reg, setReg] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sourceInfo, setSourceInfo] = useState(null);

  const setField = (k, v) => {
    onChange({ ...value, [k]: v });
  };

  const runLookup = async () => {
    setError(null);
    setSourceInfo(null);

    if (!reg.trim()) {
      setError('Please enter a UK number plate.');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please log in to use reg-plate lookup.');
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/vehicles/lookup`,
        { reg: reg.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSourceInfo(data.sources);
      const details = data.vehicle_details || {};
      if (Object.keys(details).length === 0) {
        setError('No data found for that plate - please fill the fields manually below.');
      }
      // Merge with any manually-entered values (manual entries win over the lookup)
      onChange({ ...details, ...value });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === 'RATE_LIMIT') {
        setError(`Too many lookups. Try again in ${detail.retry_after_sec || 60}s.`);
      } else if (detail?.code === 'INVALID_REG') {
        setError(detail.message);
      } else if (e?.response?.status === 401) {
        setError('Please log in to use reg-plate lookup.');
      } else {
        setError('Lookup failed - please fill the fields manually below.');
      }
    } finally {
      setLoading(false);
    }
  };

  const dvlaOK = sourceInfo?.dvla?.configured && !sourceInfo?.dvla?.error;
  const dvsaOK = sourceInfo?.dvsa?.configured && !sourceInfo?.dvsa?.error;
  const cqOK = sourceInfo?.carquery?.configured && !sourceInfo?.carquery?.error;
  const anyConfigured = sourceInfo?.dvla?.configured || sourceInfo?.dvsa?.configured;

  return (
    <Card className="border-blue-200 bg-blue-50/30" data-testid="vehicle-reg-lookup-card">
      <CardHeader>
        <CardTitle className="text-lg">Vehicle Details</CardTitle>
        <CardDescription>
          Enter your UK number plate and we'll pre-fill the specs from DVLA. Anything wrong? Just edit the fields below before publishing.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <Label htmlFor="reg-plate">UK Number Plate</Label>
            <Input
              id="reg-plate"
              type="text"
              placeholder="e.g. AB12 CDE"
              value={reg}
              onChange={(e) => setReg(e.target.value.toUpperCase())}
              maxLength={10}
              className="uppercase font-mono tracking-widest text-lg"
              data-testid="reg-plate-input"
            />
          </div>
          <Button
            type="button"
            onClick={runLookup}
            disabled={loading || reg.length < 2}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="reg-plate-lookup-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
            Look up
          </Button>
        </div>

        {error && (
          <Alert variant="destructive" data-testid="reg-lookup-error">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {sourceInfo && anyConfigured && !error && (
          <Alert className="bg-green-50 border-green-200" data-testid="reg-lookup-success">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800 flex flex-wrap gap-2 items-center">
              <span>Auto-filled from:</span>
              {dvlaOK && <Badge variant="secondary" className="bg-blue-100 text-blue-800">DVLA</Badge>}
              {dvsaOK && <Badge variant="secondary" className="bg-purple-100 text-purple-800">DVSA MOT</Badge>}
              {cqOK && <Badge variant="secondary" className="bg-orange-100 text-orange-800">CarQuery</Badge>}
              <span className="text-xs italic">- any field can be edited below.</span>
            </AlertDescription>
          </Alert>
        )}

        {sourceInfo && !anyConfigured && (
          <Alert className="bg-amber-50 border-amber-200" data-testid="reg-lookup-not-configured">
            <Info className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800 text-sm">
              Reg-plate lookup is not yet activated (DVLA/DVSA keys pending).
              Please enter the vehicle details manually below - every field is editable.
            </AlertDescription>
          </Alert>
        )}

        {/* Editable spec grid */}
        <div className="space-y-6">
          {FIELD_GROUPS.map((group) => (
            <div key={group.heading}>
              <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">
                {group.heading}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {group.fields.map(([key, label]) => (
                  <div key={key}>
                    <Label htmlFor={`spec-${key}`} className="text-xs text-gray-600">{label}</Label>
                    <Input
                      id={`spec-${key}`}
                      value={value[key] ?? ''}
                      onChange={(e) => setField(key, e.target.value)}
                      placeholder="-"
                      className="text-sm"
                      data-testid={`spec-input-${key}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
